import * as React from "react";
import {Link} from "react-router-dom";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import * as moment from "moment";
import {
    ICustomerPortalService,
    PartyProperty,
    PartyPropertyEnum,
    Policy,
    resourcesHelper,
    Types,
    INTERACTION_PROCESS_MTC_REMOVE_CAR,
    POLICY_STATUS_MTC
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {capitalize} from "../../../../../CommonFunctions";

interface IProps {
    policy: Policy;
    replacedCarPolicy?: Policy,
    changeOfVehicleLimitReached: boolean,
    renewalPaymentFailed: boolean,
    vehicleIndex: number;
    tempDriver: any;
    policies?: Policy[];
}

interface IState {
    allowGreenCardRequest: boolean;
    allowEditRegistrationNumber: boolean;
}

export class YourCarPanel extends React.Component<IProps, IState> {

    private readonly cancelled: boolean = false;
    private readonly replaced: boolean = false;
    private readonly vehicleDrivers: any[] = [];
    private readonly policyToRenderFrom: Policy = this.props.replacedCarPolicy || this.props.policy;
    private readonly alertMessage: string | undefined = undefined;
    private readonly replaceCarAlertMessage: string | undefined = undefined;
    private readonly renewalFailed: boolean;
    private readonly addressWasChanged: boolean;
    private readonly maxDriversPerCar: number = 6;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        if (this.policyToRenderFrom.status === POLICY_STATUS_MTC) {
            this.cancelled = true;
            const endDate: moment.Moment = moment.utc(this.policyToRenderFrom.endDate, moment.ISO_8601);
            if (moment(endDate).isBefore(new Date(), "day")) {
                this.alertMessage = "Sorry, due to cancellation this action is not allowed.";
            } else {
                this.alertMessage = "Sorry, you can’t edit this car because it’s getting cancelled soon.";
            }
        } else if (this.props.renewalPaymentFailed) {
            this.renewalFailed = true;
            this.alertMessage = "Due to missed payment this action is not allowed";
        } else if (this.props.replacedCarPolicy !== undefined) {
            this.replaced = true;
            this.alertMessage = "Sorry, you can’t edit this car because you’re replacing it this month.";
        } else if (this.props.changeOfVehicleLimitReached) {
            this.replaceCarAlertMessage = "Sorry, you can only replace your car up to two times per subscription month";
        }

        if (this.policyToRenderFrom.status === POLICY_STATUS_MTC && this.props.policies) {
            const activePolicies: any[] = this.props.policies.filter((policy: Policy) => policy.status !== POLICY_STATUS_MTC);
            if (activePolicies && activePolicies.length && activePolicies[0].interaction.policyHolder.address.fullAddress !== this.props.policy.interaction.policyHolder.address.fullAddress) {
                this.addressWasChanged = true;
                const endDate: string = moment(this.props.policy.interaction.headerInfo.endDate).format("DD/MM/YYYY");
                this.alertMessage = `Sorry, you can’t reactivate this car cover because you’ve made changes to your subscription since you cancelled this car cover. You can add this car back to your subscription after your cancellation on ${endDate}.`;
            }
        }

        const {interaction} = this.policyToRenderFrom;
        const vehicle: any = interaction.vehicles[0];
        const allDrivers: any[] = [interaction.policyHolder].concat(interaction.additionalDrivers || []);
        if (vehicle.driverIds) {
            this.vehicleDrivers = vehicle.driverIds
                .map((driverId: string) => allDrivers.find((driver: any) => driverId === driver.personId))
                .filter((driver: any) => driver !== undefined);
        }

        if (this.props.tempDriver) {
            this.vehicleDrivers.push(this.props.tempDriver);
        }

        this.state = {
            allowGreenCardRequest: true,
            allowEditRegistrationNumber: true
        };
    }

    public componentDidMount() {
        this.customerPortalService.getPartyPropertyByPropertyKey(PartyPropertyEnum.LAST_VRN_CHANGE_DATE_TEMPLATE.replace("%d", this.policyToRenderFrom.policyNumber))
            .subscribe((partyProperty: PartyProperty) => {
                if (partyProperty && partyProperty.value) {
                    const lastVrnChangeDate = moment(partyProperty.value, "YYYY-MM-DD");
                    const now = moment();
                    this.setState({
                        allowEditRegistrationNumber: lastVrnChangeDate.isValid() && lastVrnChangeDate.add(1, "year").isBefore(now)
                    });
                } else {
                    this.setState({allowEditRegistrationNumber: true});
                }
            });
    }

    public render() {
        const policyToRenderFrom = this.policyToRenderFrom;
        const {vehicleIndex} = this.props;
        const {interaction} = policyToRenderFrom;
        const vehicle: any = interaction.vehicles[0];

        return (
            <div className="section-panel your-car-panel">
                <img className="main-icon" src={`/${resourcesHelper.THEME}/img/wolf-car-icon.png`} alt="Vehicle icon"/>
                {this.cancelled && this.renderCancellationDisclaimer()}
                {this.replaced && this.renderReplacedCarDisclaimer()}
                <div className="section">
                    <div className="section-heading">{`${capitalize(vehicle.make)}, ${capitalize(vehicle.model)}`}</div>
                    <div className="section-contents">
                        {this.renderCarDetails()}
                    </div>
                    <div className="section-links">
                        <ButtonLink
                            id={`replaceCar${vehicleIndex}`}
                            primary={true}
                            disabled={this.cancelled || this.replaced || this.renewalFailed || this.props.changeOfVehicleLimitReached}
                            alertMessage={this.alertMessage || this.replaceCarAlertMessage}
                            to={`/journey/init/product/MotorInsurance/process/mtaReplaceCar/preset/${interaction._id}`}
                        >Replace car</ButtonLink>
                        {this.renderCancelAndReactivateButtons()}
                    </div>
                </div>
                <div className="section">
                    <div className="section-heading">Drivers</div>
                    <div className="section-contents">
                        <div className="detail">
                            {this.renderVehicleDrivers()}
                        </div>
                    </div>
                    <div className="section-links">
                        <ButtonLink
                            id={`addDriverToVehicle${vehicleIndex}`}
                            disabled={this.cancelled || this.replaced || this.renewalFailed || this.vehicleDrivers.length >= this.maxDriversPerCar}
                            alertMessage={this.alertMessage}
                            to={`/journey/init/product/MotorInsurance/process/mtaAddPermanentDriver/preset/${interaction._id}`}
                        >Add driver</ButtonLink>
                    </div>
                </div>
            </div>
        );
    }

    private renderCancelAndReactivateButtons(): JSX.Element {
        const totalRenderedPanels = this.props.policies!.length
        const notCancelledPolicyCount = this.props.policies!.filter(p => p.status !== POLICY_STATUS_MTC).length

        if (totalRenderedPanels > 1) {
            const {policy, vehicleIndex} = this.props;
            const {interaction} = policy;

            if (this.cancelled && this.props.policy.interaction.process === INTERACTION_PROCESS_MTC_REMOVE_CAR && notCancelledPolicyCount > 0) {
                return (
                    <>
                        <ButtonLink
                            id={`reactivateCarCover${vehicleIndex}`}
                            alertMessage={this.alertMessage}
                            disabled={this.addressWasChanged}
                            to={`/journey/show/product/MotorInsurance/process/mtaReinstateCarCover/initializeInteraction/${this.props.policy.interaction._id}`}
                        >Reactivate car cover</ButtonLink>
                    </>
                )
            } else if (!this.cancelled && notCancelledPolicyCount > 1) {
                return (
                    <>
                        <ButtonLink
                            id={`cancelCarCover${vehicleIndex}`}
                            disabled={this.cancelled || this.replaced || this.renewalFailed}
                            alertMessage={this.alertMessage}
                            to={`/journey/init/product/MotorInsurance/process/mtcRemoveCar/presetPage/${interaction._id}`}
                        >Cancel car cover</ButtonLink>
                    </>
                )
            }
        }

        return <></>
    }

    private renderCarDetails(): JSX.Element {
        const {vehicleIndex} = this.props;
        const vehicle: any = this.policyToRenderFrom.interaction.vehicles[0];
        return (
            <React.Fragment>
                <div className="text-center">
                    <div className="number-plate-preview mb-4 d-inline-block px-2">{vehicle.registrationNumber}</div>
                    <div className="link-edit d-inline-block ml-2">
                        <Link
                            id={`editCar${vehicleIndex}RegistrationNumber`}
                            aria-label="Edit car registration number"
                            to={`/journey/init/product/MotorInsurance/process/mtaEditVehicleRegistrationNumber/editCarRegistrationEntryPage/${this.policyToRenderFrom.interaction._id}`}
                            className={(this.cancelled || this.replaced || this.renewalFailed || !this.state.allowEditRegistrationNumber) ? "disabled" : ""}
                        >Edit</Link>
                    </div>
                </div>
                <div className="link-edit">
                    <Link
                        id={`editCar${vehicleIndex}Details`}
                        aria-label="Edit car details"
                        to={`/journey/init/product/MotorInsurance/process/mta/car${vehicleIndex}MtaEntryPage/${this.policyToRenderFrom.interaction._id}`}
                        className={(this.cancelled || this.replaced || this.renewalFailed) ? "disabled" : ""}
                    >Edit car details</Link>
                </div>
                <div className="detail">
                    <div className="detail-key">Car usage</div>
                    <div className="detail-value">
                        {vehicle.businessUse ? "Business use excluding commercial travelling" :
                            "Social domestic and pleasure including commuting to one place to work"}
                    </div>
                </div>
                <div className="detail">
                    <div className="detail-key">Compulsory excess</div>
                    {this.renderDriverCompulsoryExcess(this.policyToRenderFrom)}
                </div>
                <div className="detail">
                    <div className="detail-key">Voluntary excess</div>
                    <div className="detail-value">{`£${vehicle.excesses.voluntaryExcess}`}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Guaranteed hire car</div>
                    <div className="detail-value">{vehicle.extraProtection.courtesyCar ? "Included" : "Not Included"}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Motor legal expenses</div>
                    <div className="detail-value">{vehicle.extraProtection.legalProtection ? "Included" : "Not Included"}</div>
                </div>
            </React.Fragment>
        );
    }

    private renderDriverCompulsoryExcess(policyToRenderFrom: Policy): JSX.Element[] {
        const vehicle: any = policyToRenderFrom.interaction.vehicles[0];
        return this.vehicleDrivers.map((driver: any, index: number) => {
            const driverCompExcess: number = driver.excesses.adExcess + vehicle.excesses.adExcess;
            let driverDescription: string = "additional driver";
            if (driver.personId === vehicle.mainDriverId) {
                driverDescription = "main driver";
            } else if (this.props.tempDriver && this.props.tempDriver.personId === driver.personId) {
                this.props.tempDriver.mainDriver ? driverDescription = "temporary main driver" : driverDescription = "temporary driver";
            }

            return (
                <div className="detail-value" key={index}>
                    {`${driver.firstName} ${driver.lastName}, `}
                    <span className="font-weight-semi-bold">{driverDescription}:</span>
                    {` £${driverCompExcess}`}
                </div>
            );
        });
    }

    private renderVehicleDrivers(): JSX.Element[] {
        const vehicle = this.policyToRenderFrom.interaction.vehicles[0];

        return this.vehicleDrivers.map((driver: any, index: number) => {
            if (this.props.tempDriver && this.props.tempDriver.personId === driver.personId) {
                return (
                    <div key={index} className="detail-value">
                        {`${driver.firstName} ${driver.lastName}`} <span className="font-weight-bold">
                        {this.props.tempDriver.mainDriver ? "(temporary main driver)" : "(temporary driver)"}
                        </span>
                    </div>
                );
            } else {
                return (
                    <div key={index} className="detail-value">
                        {`${driver.firstName} ${driver.lastName}`} {driver.personId === vehicle.mainDriverId && <span className="font-weight-bold">(main driver)</span>}
                    </div>
                );
            }
        });
    }

    private renderCancellationDisclaimer(): JSX.Element {
        const endDate: moment.Moment = moment.utc(this.policyToRenderFrom.endDate, moment.ISO_8601);

        let disclaimer: string;
        if (endDate.isSame(moment(), 'day')) {
            disclaimer = `Cover ends today`;
        } else if (endDate.isBefore(moment(), 'day')) {
            disclaimer = `Cover ended on ${endDate.format("D MMMM YYYY")}`;
        } else {
            disclaimer = `Cover will end on ${endDate.format("D MMMM YYYY")}`;
        }

        return (
            <div className="warning">
                {disclaimer}
            </div>
        );
    }

    private renderReplacedCarDisclaimer(): JSX.Element {
        const {make, model, registrationNumber} = this.props.policy.insuredVehicles[0];
        const startDate: string = moment(this.props.policy.interaction.headerInfo.startDate).format("DD/MM/YYYY");

        return (
            <div className="warning banner banner-info">
                Heads up - this car will be replaced with your new {make}, {model} ({registrationNumber}) on {startDate}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/subscription/motor/yourCarsPage/YourCarPanel.tsx